import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { floorOnAbsoluteStateless as floorOnAbsolute } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useFloorOnAbsolute';
import { ConvertInvoiceTransaction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/model';
import {
  Inference,
  OcrInferenceResult,
} from '@/features/InvoiceReport/Edit/components/type';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { nullToUndefined } from '@/utils/null_to_undefined';
import { randomId } from '@moneyforward/ap-frontend-components';
import {
  ApPayeeDetail,
  ApPayeeDetailAllOfDefaultProject,
  DefaultCrItemOfPayee as ApPayeeDetailAllOfDefaultCrItem,
  DefaultCrSubItemOfPayee as ApPayeeDetailAllOfDefaultCrSubItem,
  DefaultDeptOfPayee as ApPayeeDetailAllOfDefaultDept,
  DefaultExItemOfPayee as ApPayeeDetailAllOfDefaultExItem,
  ApPayeeDetailAllOfPaymentMethod,
  OcrInvoiceInferenceResponse,
} from 'ap-openapi';
import Big from 'big.js';
import camelcaseKeys from 'camelcase-keys';
import { stringToDayjs } from 'date-util';
import dayjs, { Dayjs } from 'dayjs';
import { SelectionState } from '../Drawer/OcrReflectionDrawer/hooks/useApplicationFormSelectionManager';
// Transform API response to OcrInferenceResult
export const transformOcrApiResponse = (
  data: OcrInvoiceInferenceResponse
): OcrInferenceResult => {
  return {
    ...data,
    dueDate: data.due_date ? dayjs(data.due_date) : undefined,
    bookDate: data.book_date ? dayjs(data.book_date) : undefined,
    invoiceRegistrationNumber: data.invoice_registration_number ?? undefined,
    apPayee: data.ap_payee
      ? {
          id: data.ap_payee.id ?? undefined,
          name: data.ap_payee.name ?? undefined,
          code: data.ap_payee.code ?? undefined,
          accountNumber: data.ap_payee.account_number ?? undefined,
          bookDate: data.ap_payee.book_date
            ? dayjs(data.ap_payee.book_date)
            : undefined,
          paymentDate: data.ap_payee.payment_date
            ? dayjs(data.ap_payee.payment_date)
            : undefined,

          displayName: data.ap_payee.display_name ?? undefined,
          defaultName: data.ap_payee.default_name ?? undefined,
          defaultExItem: data.ap_payee.default_ex_item
            ? {
                id: data.ap_payee.default_ex_item.id ?? undefined,
                displayName:
                  data.ap_payee.default_ex_item.display_name ?? undefined,
              }
            : undefined,
          defaultCrItem: data.ap_payee.default_cr_item
            ? {
                id: data.ap_payee.default_cr_item.id ?? undefined,
                displayName:
                  data.ap_payee.default_cr_item.display_name ?? undefined,
              }
            : undefined,
          defaultCrSubItem: data.ap_payee.default_cr_sub_item
            ? {
                id: data.ap_payee.default_cr_sub_item.id ?? undefined,
                displayName:
                  data.ap_payee.default_cr_sub_item.display_name ?? undefined,
              }
            : undefined,
          defaultDept: data.ap_payee.default_dept
            ? {
                id: data.ap_payee.default_dept.id ?? undefined,
                displayName:
                  data.ap_payee.default_dept.display_name ?? undefined,
              }
            : undefined,
          defaultProject: data.ap_payee.default_project
            ? {
                id: data.ap_payee.default_project.id ?? undefined,
                displayName:
                  data.ap_payee.default_project.display_name ?? undefined,
              }
            : undefined,
          isTaxIncluded: data.ap_payee.is_tax_included ?? undefined,
          isWithholdingTax: data.ap_payee.is_withholding_tax ?? undefined,
          invoiceRegistrationNumber:
            data.ap_payee.invoice_registration_number ?? undefined,
          paymentMethod: data.ap_payee.payment_method ?? undefined,
          foreignCurrency: data.ap_payee.foreign_currency
            ? {
                currency: data.ap_payee.foreign_currency.currency!,
                rates: data.ap_payee.foreign_currency.rates
                  ? data.ap_payee.foreign_currency.rates.map((rate) => ({
                      startDate: dayjs(rate.start_date),
                      endDate: dayjs(rate.end_date),
                      value: rate.value ?? undefined,
                    }))
                  : [],
              }
            : undefined,
          contactOfficeMembers: data.ap_payee.contact_office_members
            ? data.ap_payee.contact_office_members.map((member) => ({
                id: member.id ?? undefined,
                name: member.name ?? undefined,
                displayName: member.display_name ?? undefined,
              }))
            : [],
        }
      : undefined,
    apInstantPayee: data.ap_instant_payee
      ? {
          name: data.ap_instant_payee.name ?? undefined,
          holderNameKana: data.ap_instant_payee.holder_name_kana ?? undefined,
          accountType: data.ap_instant_payee.account_type ?? undefined,
          number: data.ap_instant_payee.number ?? undefined,
          bank: data.ap_instant_payee.bank
            ? {
                id: data.ap_instant_payee.bank.id ?? undefined,
                name: data.ap_instant_payee.bank.name ?? undefined,
              }
            : undefined,
          bankBranch: data.ap_instant_payee.bank_branch
            ? {
                id: data.ap_instant_payee.bank_branch.id ?? undefined,
                name: data.ap_instant_payee.bank_branch.name ?? undefined,
              }
            : undefined,
        }
      : undefined,
    invoiceTransactions: data.invoice_transactions
      ? data.invoice_transactions.map((transaction) => ({
          number: transaction.number ?? undefined,
          name: transaction.name ?? undefined,
          unitValue: transaction.unit_value ?? undefined,
          quantity: transaction.quantity ?? undefined,
          totalValue: transaction.total_value_without_tax ?? undefined,
          totalTaxIncludeValue: transaction.total_value ?? undefined,
          exciseValue: transaction.excise_value ?? undefined,
          hasWithholdingIncomeTax:
            transaction.has_withholding_income_tax ?? undefined,
          withholdingIncomeTaxValue:
            transaction.withholding_income_tax_value ?? undefined,
          memo: transaction.memo ?? undefined,
          currency: transaction.currency ?? undefined,
          jpyRate: transaction.jpyrate ?? undefined,
          useCustomJpyRate: transaction.use_custom_jpy_rate ?? undefined,
          apPayee: transaction.ap_payee
            ? {
                id: transaction.ap_payee.id ?? undefined,
                name: transaction.ap_payee.name ?? undefined,
              }
            : undefined,
          apPayeeType: transaction.ap_payee_type ?? undefined,
          dept: transaction.dept
            ? {
                id: transaction.dept.id ?? undefined,
                name: transaction.dept.name ?? undefined,
              }
            : undefined,
          project: transaction.project
            ? {
                id: transaction.project.id ?? undefined,
                name: transaction.project.name ?? undefined,
              }
            : undefined,
          exItem: transaction.ex_item
            ? {
                id: transaction.ex_item.id ?? undefined,
                name: transaction.ex_item.name ?? undefined,
              }
            : undefined,
          drExcise: transaction.dr_excise
            ? {
                id: transaction.dr_excise.id ?? undefined,
                name: transaction.dr_excise.name ?? undefined,
              }
            : undefined,
          crItem: transaction.cr_item
            ? {
                id: transaction.cr_item.id ?? undefined,
                name: transaction.cr_item.name ?? undefined,
              }
            : undefined,
          crSubItem: transaction.cr_sub_item
            ? {
                id: transaction.cr_sub_item.id ?? undefined,
                name: transaction.cr_sub_item.name ?? undefined,
              }
            : undefined,
          invoiceKind: transaction.invoice_kind ?? undefined,
          dealDate: transaction.deal_date
            ? dayjs(transaction.deal_date)
            : undefined,
        }))
      : [],
    inferences: data.inferences
      ? data.inferences.map(
          (inference) =>
            ({
              ...inference,
              apPayee: inference.ap_payee
                ? {
                    source: inference.ap_payee.source,
                    score: inference.ap_payee.score || undefined,
                    reason: inference.ap_payee.reason || undefined,
                    position: inference.ap_payee.position
                      ? {
                          leftTop: inference.ap_payee.position.left_top,
                          rightTop: inference.ap_payee.position.right_top,
                          leftBottom: inference.ap_payee.position.left_bottom,
                          rightBottom: inference.ap_payee.position.right_bottom,
                        }
                      : undefined,
                  }
                : undefined,
              dueDate: inference.due_date
                ? {
                    source: inference.due_date.source,
                    score: inference.due_date.score || undefined,
                    reason: inference.due_date.reason || undefined,
                    position: inference.due_date.position
                      ? {
                          leftTop: inference.due_date.position.left_top,
                          rightTop: inference.due_date.position.right_top,
                          leftBottom: inference.due_date.position.left_bottom,
                          rightBottom: inference.due_date.position.right_bottom,
                        }
                      : undefined,
                  }
                : undefined,
              bookDate: inference.book_date
                ? {
                    source: inference.book_date.source,
                    score: inference.book_date.score || undefined,
                    reason: inference.book_date.reason || undefined,
                    position: inference.book_date.position
                      ? {
                          leftTop: inference.book_date.position.left_top,
                          rightTop: inference.book_date.position.right_top,
                          leftBottom: inference.book_date.position.left_bottom,
                          rightBottom:
                            inference.book_date.position.right_bottom,
                        }
                      : undefined,
                  }
                : undefined,
              invoiceRegistrationNumber: inference.invoice_registration_number
                ? {
                    source: inference.invoice_registration_number.source,
                    score:
                      inference.invoice_registration_number.score || undefined,
                    reason:
                      inference.invoice_registration_number.reason || undefined,
                    position: inference.invoice_registration_number.position
                      ? {
                          leftTop:
                            inference.invoice_registration_number.position
                              .left_top,
                          rightTop:
                            inference.invoice_registration_number.position
                              .right_top,
                          leftBottom:
                            inference.invoice_registration_number.position
                              .left_bottom,
                          rightBottom:
                            inference.invoice_registration_number.position
                              .right_bottom,
                        }
                      : undefined,
                  }
                : undefined,
              invoiceTransactions: inference.invoice_transactions
                ? inference.invoice_transactions.map(
                    (transaction) =>
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                      ({
                        ...nullToUndefined(
                          camelcaseKeys(transaction, { deep: true })
                        ),
                        totalValueExcludingTax:
                          transaction['total_value_without_tax'],
                      } as Inference['invoiceTransactions'][number])
                  )
                : [],
            } satisfies Inference)
        )
      : [],
    // Top-level totals
    exciseValue: data.excise_value ?? undefined,
    withholdingIncomeTaxValue: data.withholding_income_tax_value ?? undefined,
    paymentRequestTotalValue: data.payment_request_total_value ?? undefined,
    transactionDetailTotalValue:
      data.transaction_detail_total_value ?? undefined,

    // Subtotals
    subtotal10: data.subtotal10
      ? {
          totalValue: data.subtotal10.total_value ?? undefined,
          totalValueWithoutTax:
            data.subtotal10.total_value_without_tax ?? undefined,
          exciseValue: data.subtotal10.excise_value ?? undefined,
        }
      : undefined,
    subtotal8: data.subtotal8
      ? {
          totalValue: data.subtotal8.total_value ?? undefined,
          totalValueWithoutTax:
            data.subtotal8.total_value_without_tax ?? undefined,
          exciseValue: data.subtotal8.excise_value ?? undefined,
        }
      : undefined,
    subtotalTaxFree: data.subtotal_tax_free
      ? {
          totalValue: data.subtotal_tax_free.total_value ?? undefined,
          totalValueWithoutTax:
            data.subtotal_tax_free.total_value_without_tax ?? undefined,
          exciseValue: data.subtotal_tax_free.excise_value ?? undefined,
        }
      : undefined,
    reference_report: data.reference_report
      ? {
          id: data.reference_report.id ?? undefined,
          number: data.reference_report.number ?? undefined,
          title: data.reference_report.title ?? undefined,
        }
      : undefined,
  };
};

export const transformForPaymentDetailsForm = (
  invoiceTransactions: OcrInferenceResult['invoiceTransactions']
): PaymentRequestForm['invoiceTransactions'] => {
  return invoiceTransactions.map((item) => {
    const quantity = Big(item?.quantity || 1);
    const taxIncludedTotal = Big(item?.totalTaxIncludeValue || 0);
    const taxIncludedUnit = taxIncludedTotal.div(quantity);

    const formFields: Omit<
      PaymentRequestForm['invoiceTransactions'][number],
      'isCurrencyJPY'
    > = {
      // Directly visible in drawer
      ////////////////////////////////////////////////////
      dealDate: item.dealDate, // 取引日
      name: item.name, //品目
      deptName: item.dept?.name, // 費用負担部門
      exItemName: item.exItem?.name, //経費科目
      totalValue: item.totalValue, //合計
      drExciseName: item.drExcise?.name, //税区分
      exciseValue: item.exciseValue, //消費税
      projectName: item.project?.name, // プロジェクト
      crItemName: item.crItem?.name, // 貸方科目
      crSubItemName: item.crSubItem?.name, // 貸方補助科目

      // Indirectly visible in drawer (IDs related to visible values)
      ////////////////////////////////////////////////////
      deptId: item.dept?.id, // 費用負担部門
      exItemId: item.exItem?.id, //経費科目
      drExciseId: item.drExcise?.id, //税区分
      projectId: item.project?.id, // プロジェクト
      crItemId: item.crItem?.id, // 貸方科目
      crSubItemId: item.crSubItem?.id, // 貸方補助科目

      // Not visible in drawer, provided by API
      ////////////////////////////////////////////////////
      unit: item.unitValue, //単価
      quantity: quantity.toNumber(), //数量
      hasWithholdingIncomeTax: item.hasWithholdingIncomeTax, //源泉徴収の有無
      withholdingIncomeTax: item.withholdingIncomeTaxValue, // 源泉徴収税額
      memo: item.memo, // メモ
      currency: item.currency, // 通貨
      useCustomJPYRate: item.useCustomJpyRate, // 通貨レート個別設定
      jpyRate: item.jpyRate, // 為替レート

      // Not visible in drawer; Not in API;
      ////////////////////////////////////////////////////
      invoiceKind: undefined,

      // Copied from useImport
      id: 'imp' + String(Math.floor(Math.random() * 10000)),
      number: item.number ?? INVOICE_NUMBER_UNSAVED,
      rowKey: randomId('invoice-transaction', 8),

      // Copied from `useApplyPastTranssactions`
      taxIncludedUnit: floorOnAbsolute(taxIncludedUnit.toNumber()), //税込単価
      taxIncludedTotalValue: floorOnAbsolute(taxIncludedTotal.toNumber()), // 税込入力合計
    };

    return ConvertInvoiceTransaction(formFields);
  });
};

export const transformForApplicationForm = (
  ocrInferenceResult: OcrInferenceResult
): Record<keyof SelectionState, string | Dayjs | null | undefined> => {
  return {
    payeeSelected: ocrInferenceResult?.apPayee?.id ?? null,

    // TODO(james): Investigate if this is redundant.
    // Looks like it's converting Dayjs => string => Dayjs
    bookDateSelected: ocrInferenceResult?.bookDate
      ? stringToDayjs(ocrInferenceResult.bookDate.format('YYYY-MM-DD'))
      : null,
    dueDateSelected: ocrInferenceResult?.dueDate
      ? stringToDayjs(ocrInferenceResult.dueDate.format('YYYY-MM-DD'))
      : null,

    invoiceRegistrationNumberSelected:
      ocrInferenceResult?.invoiceRegistrationNumber ?? null,
    instantPayeeNameSelected: ocrInferenceResult?.apInstantPayee?.name ?? null,
    bankNameSelected: ocrInferenceResult?.apInstantPayee?.bank?.id ?? null,
    bankBranchNameSelected:
      ocrInferenceResult?.apInstantPayee?.bankBranch?.id ?? null,
    bankAccountTypeSelected:
      ocrInferenceResult?.apInstantPayee?.accountType ?? null,
    bankAccountNumberSelected:
      ocrInferenceResult?.apInstantPayee?.number ?? null,
    bankNameKanaSelected:
      ocrInferenceResult?.apInstantPayee?.holderNameKana ?? null,
    titleSelected: ocrInferenceResult?.title ?? null,
  };
};

export const transformForApplicationFormForPreview = (
  ocrInferenceResult: OcrInferenceResult
): Record<keyof SelectionState, string | Dayjs | null | undefined> => {
  return {
    payeeSelected: ocrInferenceResult?.apPayee?.name ?? null,
    bookDateSelected: ocrInferenceResult?.bookDate
      ? stringToDayjs(ocrInferenceResult.bookDate.format('YYYY-MM-DD'))
      : null,
    dueDateSelected: ocrInferenceResult?.dueDate
      ? stringToDayjs(ocrInferenceResult.dueDate.format('YYYY-MM-DD'))
      : null,
    invoiceRegistrationNumberSelected:
      ocrInferenceResult?.invoiceRegistrationNumber ?? null,
    instantPayeeNameSelected: ocrInferenceResult?.apInstantPayee?.name ?? null,
    bankNameSelected: ocrInferenceResult?.apInstantPayee?.bank?.name ?? null,
    bankBranchNameSelected:
      ocrInferenceResult?.apInstantPayee?.bankBranch?.name ?? null,
    bankAccountTypeSelected:
      ocrInferenceResult?.apInstantPayee?.accountType ?? null,
    bankAccountNumberSelected:
      ocrInferenceResult?.apInstantPayee?.number ?? null,
    bankNameKanaSelected:
      ocrInferenceResult?.apInstantPayee?.holderNameKana ?? null,
    titleSelected: ocrInferenceResult?.title ?? null,
  };
};

export const transformApPayeeForContext = (
  payeeFromOcr: OcrInferenceResult['apPayee']
): ApPayeeDetail => {
  // TODO(james): I think these assertions are necessary because of mistakes in `ap_fe.yaml`
  // Ideally `ap_fe.yaml` should be correct. If it is correct already, then we will have
  // to add some extra checks to this transform function.

  // TODO(james): This function is basically just undoing `transformOcrApiResponse`
  // There's a lot of room to refactor the way we handle this data

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const default_cr_item = payeeFromOcr?.defaultCrItem
    ?.displayName as ApPayeeDetailAllOfDefaultCrItem;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const default_cr_sub_item = payeeFromOcr?.defaultCrItem
    ?.id as ApPayeeDetailAllOfDefaultCrSubItem;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const default_dept = payeeFromOcr?.defaultDept
    ?.displayName as ApPayeeDetailAllOfDefaultDept;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const default_ex_item = payeeFromOcr?.defaultExItem
    ?.displayName as ApPayeeDetailAllOfDefaultExItem;
  const default_project: ApPayeeDetailAllOfDefaultProject =
    payeeFromOcr?.defaultProject
      ? {
          id: payeeFromOcr.defaultProject.id,
          display_name: payeeFromOcr.defaultProject.displayName,
        }
      : null;
  const payment_method =
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    payeeFromOcr?.paymentMethod as ApPayeeDetailAllOfPaymentMethod;

  return {
    account_number: payeeFromOcr?.accountNumber,
    code: payeeFromOcr?.code,
    name: payeeFromOcr?.name,
    priority: undefined,
    book_date: payeeFromOcr?.bookDate?.format('YYYY-MM-DD'),
    contact_office_members: payeeFromOcr?.contactOfficeMembers,
    default_cr_item,
    default_cr_item_id: payeeFromOcr?.defaultCrItem?.id,
    default_cr_sub_item,
    default_cr_sub_item_id: payeeFromOcr?.defaultCrSubItem?.id,
    default_dept,
    default_dept_id: payeeFromOcr?.defaultDept?.id,
    default_ex_item,
    default_ex_item_id: payeeFromOcr?.defaultExItem?.id,
    default_name: payeeFromOcr?.defaultName,
    default_project,
    display_name: payeeFromOcr?.displayName,
    foreign_currency: payeeFromOcr?.foreignCurrency,
    id: payeeFromOcr?.id,
    invoice_registration_number: payeeFromOcr?.invoiceRegistrationNumber,
    is_tax_included: payeeFromOcr?.isTaxIncluded,
    is_withholding_tax: payeeFromOcr?.isWithholdingTax,
    payment_date: payeeFromOcr?.paymentDate?.format('YYYY-MM-DD'),
    payment_method,
  };
};

export const getTransformedValue = (
  key: keyof SelectionState,
  ocrInferenceResult: OcrInferenceResult | null | undefined
): string | Dayjs | null | undefined => {
  if (!ocrInferenceResult) {
    return null;
  }

  const transformedData =
    transformForApplicationFormForPreview(ocrInferenceResult);
  return transformedData[key];
};
